import type { ReactNode } from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Drawer,
  IconButton,
  InputAdornment,
  InputBase,
  ListItemText,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFormContext, useController } from 'react-hook-form';
import { useI18n } from 'iomart-hooks';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import TextMaxLine from 'src/components/text-max-line';
import { getFormattedValue } from 'src/utils/common';
import { UseHook } from 'src/@types/common';
import type { Columns } from 'src/@types/common';

// ----------------------------------------------------------------------

const DictionaryCellContent = ({ row, col }: { row: any; col: Columns }) => {
  const { name, subname, format, subFormat, params, nameLines, subNameLines } = col;
  const content = getFormattedValue(row, name, format, params);
  const mainStr = String(
    (typeof content === 'string' ? content : content ?? '-') || '-'
  );
  const subContent = subname ? getFormattedValue(row, subname, subFormat, params) : null;
  const subStr = subContent != null ? String(subContent) || '-' : '';
  const tooltipTitle = subStr ? `${mainStr}\n${subStr}` : mainStr;
  const lineSx = { minWidth: 0, width: '100%' };

  const cellContent = subname ? (
    <Box sx={{ minWidth: 0, maxWidth: 1, overflow: 'hidden', width: 1 }}>
      <TextMaxLine line={nameLines ?? 1} variant="caption" sx={lineSx}>
        {mainStr}
      </TextMaxLine>
      <TextMaxLine
        line={subNameLines ?? 1}
        variant="caption"
        sx={{ ...lineSx, color: 'text.disabled', mt: 0.25 }}
      >
        {subStr}
      </TextMaxLine>
    </Box>
  ) : (
    <TextMaxLine line={nameLines ?? 1} variant="caption" sx={lineSx}>
      {mainStr}
    </TextMaxLine>
  );

  return (
    <Tooltip
      title={<Box component="span" sx={{ whiteSpace: 'pre-line' }}>{tooltipTitle}</Box>}
      placement="top-start"
      enterDelay={400}
    >
      <Box component="span" sx={{ display: 'block', minWidth: 0, overflow: 'hidden' }}>
        {cellContent}
      </Box>
    </Tooltip>
  );
};

// ----------------------------------------------------------------------

const AutocompleteListbox = forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
  (props, ref) => {
    const { className, style, ...rest } = props;
    // MUI даёт listbox overflow:auto — тогда скролл на ul (нативный).
    // Переносим maxHeight/overflow на Scrollbar, ul — без overflow, чтобы скролл был через SimpleBar (как в Drawer).
    return (
      <Scrollbar
        className={className}
        mobileBehavior="custom"
        sx={{ maxHeight: '40vh', overflow: 'hidden' }}
      >
        <ul ref={ref} {...rest} style={{ ...style, overflow: 'visible', maxHeight: 'none' }} />
      </Scrollbar>
    );
  }
);
AutocompleteListbox.displayName = 'AutocompleteListbox';

// ----------------------------------------------------------------------

type AppliedReferenceFilter = { key: string; label: string };

type Props = {
  name: string;
  label: string;
  useHook: UseHook;
  optionsLabel?: string;
  getOptionLabel?: (option: any) => string;
  columns: Columns[];
  multiple?: boolean;
  filters?: Record<string, any>;
  /** Конфиг для отображения тегов применённых фильтров в Drawer (как в скроллерах) */
  appliedReferenceFilters?: AppliedReferenceFilter[];
  searchName?: string;
  pageSize?: number;
  disabled?: boolean;
  /** Ширина Drawer: { xs: '100%', sm: 560 } */
  drawerWidth?: Record<string, string | number>;
  /** Кнопки/действия в шапке Drawer (например кнопка «Добавить товар» для быстрого создания) */
  headerActions?: ReactNode;
  /** При изменении — перезагрузка списка в Drawer (например после добавления элемента) */
  refreshTrigger?: number;
  /** Вызывается после setValue при выборе значения */
  onChange?: (value: any) => void;
};

export default function RHFDictionary({
  name,
  label,
  useHook,
  optionsLabel = 'name',
  getOptionLabel: getOptionLabelProp,
  columns,
  multiple = false,
  filters = {},
  appliedReferenceFilters = [],
  searchName = 'search',
  pageSize = 10,
  disabled,
  drawerWidth = { xs: '100%', sm: 560 },
  headerActions,
  refreshTrigger = 0,
  onChange,
}: Props) {
  const { t } = useI18n(false);
  const { control, setValue } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: multiple ? [] : null,
  });
  const { getList } = useHook(false);
  const getListRef = useRef(getList);
  getListRef.current = getList;

  const [drawerOpen, setDrawerOpen] = useState(false);
  // Чтобы при закрытии Drawer не возникало предупреждений про aria-hidden:
  // сохраняем элемент, на котором был фокус до открытия справочника,
  // и возвращаем фокус обратно после закрытия.
  const lastActiveElementRef = useRef<HTMLElement | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [autocompleteOptions, setAutocompleteOptions] = useState<any[]>([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue, setDebouncedInputValue] = useState('');
  const debouncedSetInput = useMemo(
    () =>
      debounce((v: string) => {
        setDebouncedInputValue(v);
      }, 400),
    []
  );
  useEffect(() => {
    debouncedSetInput(inputValue);
    return () => debouncedSetInput.cancel();
  }, [inputValue, debouncedSetInput]);
  const debouncedSetSearch = useMemo(
    () =>
      debounce((v: string) => {
        setDebouncedSearch(v);
        setPage(0);
      }, 400),
    []
  );
  useEffect(() => {
    debouncedSetSearch(searchInput);
    return () => debouncedSetSearch.cancel();
  }, [searchInput, debouncedSetSearch]);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const loadItems = useCallback(() => {
    setLoading(true);
    const raw = debouncedSearch.trim()
      ? { [searchName]: debouncedSearch.trim(), ...filtersRef.current }
      : filtersRef.current;
    // В API передаём только поля, допустимые в FilterInput (без *_display для подписей тегов)
    const searchFilters = Object.fromEntries(
      Object.entries(raw).filter(([key]) => !key.endsWith('_display'))
    );
    getListRef
      .current({
        paging: { page, size: pageSize },
        filters: searchFilters,
        category: 'active',
      })
      .then((res: { items: any[]; total?: number; counters?: { active?: number } }) => {
        const receivedItems = res.items || [];
        // category: 'active' — пагинация по активным, берём counters.active, иначе total
        const receivedTotal =
          res.counters?.active ?? res.total ?? res.items?.length ?? 0;
        setItems(receivedItems);
        setTotal(receivedTotal);
        // Если вернули пусто на не первой странице — лишняя страница в пагинации, уходим на предыдущую
        if (receivedItems.length === 0 && page > 0) {
          setPage((p) => Math.max(0, p - 1));
        }
      })
      .catch(() => {
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [page, pageSize, searchName, debouncedSearch]);

  useEffect(() => {
    if (drawerOpen) {
      setPage(0);
    }
  }, [drawerOpen]);

  useEffect(() => {
    if (drawerOpen) {
      loadItems();
    }
  }, [drawerOpen, page, loadItems, refreshTrigger]);

  // Поиск для Autocomplete (при вводе в поле)
  useEffect(() => {
    if (!autocompleteOpen) return;
    setAutocompleteLoading(true);
    const raw = debouncedInputValue.trim()
      ? { [searchName]: debouncedInputValue.trim(), ...filtersRef.current }
      : filtersRef.current;
    const searchFilters = Object.fromEntries(
      Object.entries(raw).filter(([key]) => !key.endsWith('_display'))
    );
    getListRef
      .current({
        paging: { page: 0, size: 15 },
        filters: searchFilters,
        category: 'active',
      })
      .then((res: { items: any[] }) => {
        setAutocompleteOptions(res.items ?? []);
      })
      .catch(() => setAutocompleteOptions([]))
      .finally(() => setAutocompleteLoading(false));
  }, [autocompleteOpen, debouncedInputValue, searchName]);

  const getOptionLabel = (opt: any) => {
    if (opt == null) return '';
    const raw = (getOptionLabelProp ?? ((o: any) => (o && o[optionsLabel]) ?? ''))(opt);
    return raw === '#undefined' || raw === '#null' ? '' : raw;
  };
  const { value } = field;
  const selectedIds = multiple ? (Array.isArray(value) ? value.map((v: any) => v?.id).filter(Boolean) : []) : [];

  const displayColumns = columns.filter(
    (col) => !['status'].includes(col.name) && col.groupType !== 'statuses'
  );

  const getCellSx = (col: Columns & { minWidth?: number; maxWidth?: number }, extra: Record<string, any> = {}) => {
    const { width, minWidth, maxWidth } = col;
    return {
      ...(width !== undefined ? { width } : {}),
      ...(minWidth !== undefined ? { minWidth } : { minWidth: 0 }),
      ...(maxWidth !== undefined ? { maxWidth } : {}),
      overflow: 'hidden',
      ...extra,
    };
  };

  const handleSelect = useCallback(
    (row: any) => {
      if (multiple) {
        const current = Array.isArray(value) ? value : [];
        const idx = current.findIndex((v: any) => v?.id === row.id);
        const next =
          idx >= 0 ? current.filter((_: any, i: number) => i !== idx) : [...current, row];
        setValue(name, next, { shouldValidate: true, shouldDirty: true });
        onChange?.(next);
      } else {
        setValue(name, row, { shouldValidate: true, shouldDirty: true });
        onChange?.(row);
        setDrawerOpen(false);
      }
    },
    [multiple, value, name, setValue, onChange]
  );

  const handleCheckboxChange = useCallback(
    (row: any, checked: boolean) => {
      if (!multiple) return;
      const current = Array.isArray(value) ? value : [];
      if (checked) {
        setValue(
          name,
          current.some((v: any) => v?.id === row.id) ? current : [...current, row],
          { shouldValidate: true, shouldDirty: true }
        );
      } else {
        setValue(
          name,
          current.filter((v: any) => v?.id !== row.id),
          { shouldValidate: true, shouldDirty: true }
        );
      }
    },
    [multiple, value, name, setValue]
  );

  const handleSelectAllInPage = useCallback(() => {
    if (!multiple || items.length === 0) return;
    const current = Array.isArray(value) ? value : [];
    const currentIds = new Set(current.map((v: any) => v?.id).filter(Boolean));
    const allOnPageSelected = items.every((row: any) => currentIds.has(row.id));
    if (allOnPageSelected) {
      const idsOnPage = new Set(items.map((r: any) => r.id));
      setValue(
        name,
        current.filter((v: any) => !idsOnPage.has(v?.id)),
        { shouldValidate: true, shouldDirty: true }
      );
    } else {
      const toAdd = items.filter((row: any) => !currentIds.has(row.id));
      setValue(name, [...current, ...toAdd], { shouldValidate: true, shouldDirty: true });
    }
  }, [multiple, items, value, name, setValue]);

  const selectedItems = useMemo(
    () =>
      multiple
        ? (Array.isArray(value) ? value : []).filter(Boolean)
        : value ? [value] : [],
    [multiple, value]
  );

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const tableMinWidth =
    displayColumns.reduce((sum, col) => {
      const w = (col as any).width ?? (col as any).minWidth ?? 80;
      return sum + (Number.isFinite(w) ? w : 80);
    }, multiple ? 48 : 48);

  const autocompleteValue = multiple ? selectedItems : (value || null);

  const autocompleteOptionsWithValue = useMemo(() => {
    const vals = multiple
      ? (Array.isArray(autocompleteValue) ? autocompleteValue : []).filter((v: any) => v?.id)
      : autocompleteValue?.id ? [autocompleteValue] : [];
    if (vals.length === 0) return autocompleteOptions;
    const existingIds = new Set(autocompleteOptions.map((o: any) => o?.id).filter(Boolean));
    const toAdd = vals.filter((v: any) => !existingIds.has(v?.id));
    return toAdd.length === 0 ? autocompleteOptions : [...toAdd, ...autocompleteOptions];
  }, [autocompleteOptions, autocompleteValue, multiple]);

  const handleAutocompleteChange = useCallback(
    (event: any, newValue: any, reason?: any) => {
      if (multiple) {
        const next = Array.isArray(newValue) ? newValue : [];
        setValue(name, next, {
          shouldValidate: true,
          shouldDirty: true,
        });
        onChange?.(next);
      } else {
        const valId = value && typeof value === 'object' ? value.id : value;
        const newId = newValue && typeof newValue === 'object' ? newValue.id : newValue;
        const isSame = valId != null && newId != null && String(valId) === String(newId);

        // Autocomplete иногда дергает onChange не по клику (при смене options/value),
        // и это может приводить к "setState during render". Обрабатываем только явный выбор.
        if (reason && reason !== 'selectOption') return;
        if (isSame) return;

        setValue(name, newValue, { shouldValidate: true, shouldDirty: true });
        onChange?.(newValue);
      }
    },
    [multiple, value, name, setValue, onChange]
  );

  return (
    <>
      <Autocomplete
        multiple={multiple}
        size="small"
        value={autocompleteValue}
        options={autocompleteOptionsWithValue}
        loading={autocompleteLoading}
        open={autocompleteOpen}
        onOpen={() => !disabled && setAutocompleteOpen(true)}
        onClose={() => setAutocompleteOpen(false)}
        inputValue={inputValue}
        onInputChange={(_, v) => setInputValue(v)}
        onChange={handleAutocompleteChange}
        filterOptions={(opts) => opts}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
        ListboxComponent={AutocompleteListbox}
        disabled={disabled}
        disableClearable
        blurOnSelect
        renderOption={(props, option) => {
          const isSelected = selectedIds.includes(option.id);
          return (
            <li {...props} key={option.id} style={{ ...props.style, display: 'flex', alignItems: 'center' }}>
              <ListItemText
                primary={getOptionLabel(option)}
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: isSelected ? { fontWeight: 600, color: 'primary.main' } : undefined,
                }}
                sx={{ flex: 1, minWidth: 0 }}
              />
              {multiple && (
                <Box sx={{ ml: 1, flexShrink: 0 }}>
                  {isSelected ? (
                    <Iconify icon="eva:checkmark-circle-2-fill" width={16} color="primary.main" />
                  ) : null}
                </Box>
              )}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={field.ref}
            name={field.name}
            inputProps={{
              ...params.inputProps,
              onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                field.onBlur();
                params.inputProps?.onBlur?.(e);
              },
            }}
            label={t(label)}
            error={!!error}
            helperText={error?.message}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputBase-input': { font: 'menu' } }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end" sx={{ marginRight: 0 }}>
                  <Tooltip title={t('Dictionary')}>
                    <span style={{ display: 'inline-flex' }}>
                      <IconButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!disabled) {
                            lastActiveElementRef.current = document.activeElement as HTMLElement | null;
                            setDrawerOpen(true);
                          }
                        }}
                        disabled={disabled}
                        edge="end"
                        size="small"
                        sx={{ marginRight: -5, marginLeft: 0 }}
                      >
                        <Iconify icon="mdi:book-open-variant" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSearchInput('');
          setTimeout(() => lastActiveElementRef.current?.focus?.(), 0);
        }}
        PaperProps={{
          sx: {
            width: drawerWidth,
            maxWidth: '100%',
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6">{t(label)}</Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {headerActions}
                <Tooltip title={t('Close')}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDrawerOpen(false);
                      setTimeout(() => lastActiveElementRef.current?.focus?.(), 0);
                    }}
                  >
                    <Iconify icon="eva:close-fill" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
            <InputBase
              fullWidth
              placeholder={t('Search') + '...'}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
              sx={{ borderRadius: 1, bgcolor: 'background.neutral', px: 1.5, py: 1 }}
            />
            {/* Теги применённых фильтров (как в скроллерах) */}
            {(debouncedSearch.trim() || appliedReferenceFilters.some(({ key }) => filters[key] != null)) && (
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
                {debouncedSearch.trim() && (
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={`${t('Search')}: ${debouncedSearch.trim()}`}
                    onDelete={() => setSearchInput('')}
                  />
                )}
                {appliedReferenceFilters.map(({ key, label: filterLabel }) => {
                  const valueId = filters[key];
                  if (valueId == null) return null;
                  const displayName = filters[`${key}_display`] ?? `#${valueId}`;
                  return (
                    <Chip
                      key={key}
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={`${t(filterLabel)}: ${displayName}`}
                    />
                  );
                })}
              </Stack>
            )}
          </Box>

          <Scrollbar mobileBehavior="custom" sx={{ flex: 1, minHeight: 0 }}>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('Loading')}...
                </Typography>
              </Box>
            ) : items.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 6,
                  px: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('No data')}
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ overflow: 'visible' }}>
                <Table size="small" stickyHeader sx={{ minWidth: tableMinWidth, tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: 48, minWidth: 48 }} />
                    {displayColumns.map((col) => (
                      <col
                        key={col.name}
                        style={{
                          width: (col as any).width ?? (col as any).minWidth ?? 100,
                          minWidth: (col as any).minWidth ?? 80,
                        }}
                      />
                    ))}
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        padding="checkbox"
                        sx={{
                          width: 48,
                          minWidth: 48,
                          maxWidth: 48,
                          position: 'sticky',
                          top: 0,
                          zIndex: 2,
                          backgroundColor: 'background.paper',
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          sx={{ ml: multiple ? -0.9 : -0.2 }}
                        >
                          {multiple && (
                            <Checkbox
                              checked={
                                items.length > 0 &&
                                items.every((row: any) => selectedIds.includes(row.id))
                              }
                              indeterminate={
                                items.length > 0 &&
                                items.some((row: any) => selectedIds.includes(row.id)) &&
                                !items.every((row: any) => selectedIds.includes(row.id))
                              }
                              onChange={handleSelectAllInPage}
                              title={t('Select all')}
                            />
                          )}
                          <Tooltip title={t('Clear')}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setValue(name, multiple ? [] : null, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  });
                                  if (!multiple) setDrawerOpen(false);
                                }}
                                disabled={
                                  multiple ? selectedIds.length === 0 : !value
                                }
                                sx={{ p: 0.25 }}
                              >
                                <Iconify icon="eva:close-circle-outline" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      {displayColumns.map((col) => (
                        <TableCell key={col.name} align={col.align} sx={getCellSx(col)}>
                          {t(col.label)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((row) => {
                      const isSelected = multiple
                        ? selectedIds.includes(row.id)
                        : value?.id === row.id;
                      return (
                        <TableRow
                          key={row.id}
                          hover
                          selected={isSelected}
                          onClick={() => !multiple && handleSelect(row)}
                          sx={{ cursor: multiple ? undefined : 'pointer' }}
                        >
                          {multiple ? (
                            <TableCell
                              padding="checkbox"
                              onClick={(e) => e.stopPropagation()}
                              sx={{ position: 'relative', zIndex: 1 }}
                            >
                              <Checkbox
                                checked={isSelected}
                                onChange={(_, checked) => handleCheckboxChange(row, checked)}
                              />
                            </TableCell>
                          ) : (
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <Tooltip title={t('Select')}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleSelect(row)}
                                  color={isSelected ? 'primary' : 'default'}
                                >
                                  <Iconify
                                    icon={isSelected ? 'eva:checkmark-circle-2-fill' : 'eva:plus-circle-outline'}
                                  />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                          {displayColumns.map((col) => (
                            <TableCell key={col.name} align={col.align} sx={getCellSx(col)}>
                              <DictionaryCellContent row={row} col={col} />
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Scrollbar>

          {totalPages > 1 && (
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(_, p) => setPage(p - 1)}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
}
